import React, { useRef, useEffect, useState, useCallback } from 'react';
import { X, Camera, RefreshCw } from 'lucide-react';
import { getDefaultLocationOptionStyle, getLocationDisplayName, getLocationOptionStyle, isLocationActive } from '../utils/locationDisplay';

interface Location {
  id: number;
  name: string;
  isActive?: boolean;
}

interface ScheduleOption {
  id: number;
  name: string;
}

interface ClockInModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  action: 'clockIn' | 'clockOut';
  employeeId: number;
  locations: Location[];
  selectedLocationId: number | null;
  onLocationChange: (id: number) => void;
  schedules?: ScheduleOption[];
  selectedScheduleId?: number | null;
  onScheduleChange?: (id: number) => void;
  requireScheduleSelection?: boolean;
  onSubmit: (imageBase64: string, locationId: number, skipFaceVerification: boolean, scheduleId?: number) => Promise<void>;
  allowFaceBypass?: boolean;
  prefilledImageBase64?: string | null;
  prefilledVerified?: boolean;
  skipFaceVerificationOnSubmit?: boolean;
}

const ClockInModal: React.FC<ClockInModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  action,
  employeeId,
  locations,
  selectedLocationId,
  onLocationChange,
  schedules = [],
  selectedScheduleId,
  onScheduleChange,
  requireScheduleSelection = false,
  onSubmit,
  allowFaceBypass = false,
  prefilledImageBase64 = null,
  prefilledVerified = false,
  skipFaceVerificationOnSubmit = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [pendingImageBase64, setPendingImageBase64] = useState<string | null>(null);
  const [recognizedEmployee, setRecognizedEmployee] = useState<{ id: number; firstName: string; lastName: string } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const startCamera = useCallback(async () => {
    if (!isOpen) return;
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err: any) {
      console.error('Camera error:', err);
      setCameraError(err?.message || 'Could not access camera. Please allow camera permissions.');
    }
  }, [isOpen]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, startCamera, stopCamera]);

  useEffect(() => {
    if (!isOpen) return;
    if (prefilledImageBase64) {
      setCapturedImage(`data:image/jpeg;base64,${prefilledImageBase64}`);
      setPendingImageBase64(prefilledImageBase64);
      if (prefilledVerified) {
        setRecognizedEmployee({ id: employeeId, firstName: '', lastName: '' });
      }
      setError(null);
    }
  }, [isOpen, prefilledImageBase64, prefilledVerified, employeeId]);

  const capturePhoto = async () => {
    if (!videoRef.current || !streamRef.current) {
      setError('Camera not ready');
      return;
    }
    setError(null);
    setIsProcessing(true);
    try {
      await new Promise(r => setTimeout(r, 150));
      const video = videoRef.current;
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) {
        setError('Camera not ready. Please wait a moment and try again.');
        return;
      }
      const size = Math.min(vw, vh);
      const sx = (vw - size) / 2;
      const sy = (vh - size) / 2;
      const canvas = document.createElement('canvas');
      canvas.width = 640;
      canvas.height = 640;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');
      ctx.drawImage(video, sx, sy, size, size, 0, 0, 640, 640);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const base64 = dataUrl.split(',')[1] || dataUrl;
      setCapturedImage(dataUrl);
      setPendingImageBase64(base64);
      await processFaceRecognition(base64);
    } catch (err: any) {
      setError(err?.message || 'Failed to capture photo');
    } finally {
      setIsProcessing(false);
    }
  };

  const processFaceRecognition = async (imageBase64: string) => {
    if (allowFaceBypass) {
      setRecognizedEmployee({ id: employeeId, firstName: '', lastName: '' });
      return;
    }
    setIsRecognizing(true);
    setRecognizedEmployee(null);
    try {
      const { faceApi } = await import('../services/api');
      const result = await faceApi.recognize({ imageBase64 });
      if (!result.success || !result.employee) {
        setError(result.message || 'Face not recognized. Please enroll your face first.');
        setRecognizedEmployee(null);
        return;
      }
      if (result.employee.id !== employeeId) {
        setError('Face recognition did not match your account. Please try again.');
        setRecognizedEmployee(null);
        return;
      }
      setRecognizedEmployee({
        id: result.employee.id,
        firstName: result.employee.firstName,
        lastName: result.employee.lastName,
      });
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Face recognition failed');
      setRecognizedEmployee(null);
    } finally {
      setIsRecognizing(false);
    }
  };

  const handleSubmit = async () => {
    if (!pendingImageBase64) {
      setError('Please capture a photo first');
      return;
    }
    if (!effectiveLocationId) {
      setError(action === 'clockIn' ? 'Please select a location' : 'Location is required. Please contact administrator.');
      return;
    }
    if (isEffectiveLocationInactive) {
      setError('This location is inactive. Clock in/out is not allowed.');
      return;
    }
    if (!allowFaceBypass && !skipFaceVerificationOnSubmit && !recognizedEmployee) {
      setError('Please capture a photo for face verification first');
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      const scheduleId =
        action === 'clockIn' && requireScheduleSelection && schedules.length > 1
          ? (selectedScheduleId ?? schedules[0]?.id)
          : schedules.length === 1
            ? schedules[0].id
            : undefined;
      await onSubmit(pendingImageBase64, effectiveLocationId, allowFaceBypass || skipFaceVerificationOnSubmit, scheduleId);
      onSuccess();
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      const detail = data?.error;
      const message = data?.message || err?.message || 'Failed to record attendance';
      setError(detail ? `${message}: ${detail}` : message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setPendingImageBase64(null);
    setRecognizedEmployee(null);
    setError(null);
  };

  const handleClose = () => {
    handleRetake();
    stopCamera();
    onClose();
  };

  if (!isOpen) return null;

  const hasMultipleLocations = action === 'clockIn' && locations.length > 1;
  const hasMultipleSchedules = action === 'clockIn' && requireScheduleSelection && schedules.length > 1;
  const effectiveLocationId = selectedLocationId ?? (locations[0]?.id ?? null);
  const effectiveScheduleId = selectedScheduleId ?? (schedules[0]?.id ?? null);
  const selectedLocation = locations.find((loc) => loc.id === effectiveLocationId) ?? null;
  const isEffectiveLocationInactive = selectedLocation != null && !isLocationActive(selectedLocation);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-800">
            {action === 'clockIn' ? 'Clock In' : 'Clock Out'} - Face Verification
          </h2>
          <button onClick={handleClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {action === 'clockIn' && hasMultipleLocations && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Location *</label>
              <select
                value={effectiveLocationId ?? ''}
                onChange={e => onLocationChange(Number(e.target.value))}
                className={`w-full px-3 py-2 border border-neutral-300 rounded-md ${isEffectiveLocationInactive ? 'text-red-600' : ''}`}
              >
                <option value="" style={getDefaultLocationOptionStyle()}>Select location...</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id} style={getLocationOptionStyle(loc)}>
                    {getLocationDisplayName(loc)}
                  </option>
                ))}
              </select>
              {isEffectiveLocationInactive && (
                <p className="mt-1 text-xs text-red-600">This location is inactive. Please choose an active location to clock in.</p>
              )}
            </div>
          )}

          {hasMultipleSchedules && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Schedule *</label>
              <select
                value={effectiveScheduleId ?? ''}
                onChange={e => onScheduleChange?.(Number(e.target.value))}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              >
                <option value="">Select schedule...</option>
                {schedules.map(sched => (
                  <option key={sched.id} value={sched.id}>{sched.name}</option>
                ))}
              </select>
            </div>
          )}

          {!capturedImage ? (
            <div className="relative aspect-square bg-neutral-900 rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
              />
              {cameraError && (
                <div className="absolute inset-0 flex items-center justify-center bg-neutral-800 text-white text-center p-4">
                  {cameraError}
                </div>
              )}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                <button
                  onClick={capturePhoto}
                  disabled={isProcessing || !!cameraError}
                  className="flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-lg hover:bg-neutral-100 disabled:opacity-50"
                >
                  <Camera className="h-6 w-6 text-neutral-700" />
                  <span className="font-medium">Capture Photo</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-neutral-200">
                <img src={capturedImage} alt="Captured" className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
                {isRecognizing && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <RefreshCw className="h-12 w-12 text-white animate-spin" />
                  </div>
                )}
              </div>
              {recognizedEmployee && (
                <div className="px-3 py-2 bg-green-50 text-green-800 rounded-lg text-sm font-medium">
                  ✓ Face verified
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleRetake}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                >
                  Retake
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isProcessing || isEffectiveLocationInactive || (!allowFaceBypass && !skipFaceVerificationOnSubmit && !recognizedEmployee) || !effectiveLocationId || (hasMultipleSchedules && !effectiveScheduleId)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {isProcessing ? 'Processing...' : action === 'clockIn' ? 'Clock In' : 'Clock Out'}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClockInModal;
